import React, { useState, useEffect } from 'react';
import { Table, Input, Button, Pagination, message, Modal, Spin, Select } from 'antd';
import { DeleteOutlined, CopyOutlined } from '@ant-design/icons';
import { getIPBlacklistList, deleteIPBlacklist, createIPBlacklist } from '../../api/ipBlacklist';
import { copyToClipboard } from '../../utils/clipboard';
import './IPBlackList.css';

const IPBlackList = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [modalVisible, setModalVisible] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);
    const [ipText, setIpText] = useState('');
    const [deleteLoading, setDeleteLoading] = useState(false);

    // 搜索过滤条件
    const [filters, setFilters] = useState({
        ip: '',
    });

    // 地区搜索类型
    const [locationSearchType, setLocationSearchType] = useState('province'); // 'province', 'city', 'district'
    const [locationSearchValue, setLocationSearchValue] = useState('');

    // 获取列表数据
    const fetchData = async (page = 1, size = 10) => {
        setLoading(true);
        try {
            const params = {
                page: page,
                page_size: size,
                ip: filters.ip,
            };

            // 根据搜索类型添加地区过滤参数
            if (locationSearchValue) {
                if (locationSearchType === 'province') {
                    params.province = locationSearchValue;
                } else if (locationSearchType === 'city') {
                    params.city = locationSearchValue;
                } else if (locationSearchType === 'district') {
                    params.district = locationSearchValue;
                }
            }

            const res = await getIPBlacklistList(params);
            if (res.code === 0) {
                setData(res.data.list);
                setTotal(res.data.total);
            } else {
                message.error(res.message || '获取列表失败');
            }
        } catch (error) {
            console.error('Fetch IP blacklist failed:', error);
            message.error(error.message || '获取列表失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(currentPage, pageSize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 处理搜索
    const handleSearch = () => {
        setCurrentPage(1);
        fetchData(1, pageSize);
    };

    // 处理重置
    const handleReset = () => {
        setFilters({ ip: '' });
        setLocationSearchValue('');
        setLocationSearchType('province');
        setCurrentPage(1);
        setLoading(true);
        getIPBlacklistList({ page: 1, page_size: pageSize })
            .then(res => {
                if (res.code === 0) {
                    setData(res.data.list);
                    setTotal(res.data.total);
                }
            })
            .finally(() => setLoading(false));
    };

    // 分页变化
    const onPageChange = (page, size) => {
        setCurrentPage(page);
        setPageSize(size);
        fetchData(page, size);
    };

    // 删除IP
    const handleDelete = (id) => {
        Modal.confirm({
            title: '确认删除',
            content: '确定要删除这个IP吗？',
            okText: '确定',
            cancelText: '取消',
            onOk: async () => {
                setDeleteLoading(true);
                try {
                    const res = await deleteIPBlacklist(id);
                    if (res.code === 0) {
                        message.success('删除成功');
                        fetchData(currentPage, pageSize);
                    } else {
                        message.error(res.message || '删除失败');
                    }
                } catch (error) {
                    console.error('Delete failed:', error);
                    message.error(error.message || '删除失败');
                } finally {
                    setDeleteLoading(false);
                }
            },
        });
    };

    // 处理添加IP
    const handleAddIP = async () => {
        if (!ipText.trim()) {
            message.error('请输入IP地址');
            return;
        }

        // 解析IP列表
        const ips = ipText
            .split('\n')
            .map(ip => ip.trim())
            .filter(ip => ip.length > 0);

        if (ips.length === 0) {
            message.error('请输入有效的IP地址');
            return;
        }

        setModalLoading(true);
        try {
            const res = await createIPBlacklist(ips);
            if (res.code === 0) {
                message.success('添加成功');
                setModalVisible(false);
                setIpText('');
                setCurrentPage(1);
                fetchData(1, pageSize);
            } else {
                message.error(res.message || '添加失败');
            }
        } catch (error) {
            console.error('Create IP failed:', error);
            message.error(error.message || '添加失败');
        } finally {
            setModalLoading(false);
        }
    };

    // 表格列定义
    const columns = [
        {
            title: 'IP',
            dataIndex: 'ip',
            key: 'ip',
            width: 150,
            render: (text, record) => (
                <span style={{ display: 'flex', alignItems: 'center' }}>
                    {text}
                    <CopyOutlined
                        style={{ marginLeft: 8, cursor: 'pointer', color: '#1890ff' }}
                        onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(text);
                        }}
                    />
                </span>
            ),
        },
        {
            title: '归属地',
            key: 'location',
            width: 200,
            render: (_, record) => (
                <span>
                    {record.province && `${record.province}`}
                    {record.city && ` ${record.city}`}
                    {record.district && ` ${record.district}`}
                </span>
            ),
        },
        {
            title: '添加时间',
            dataIndex: 'create_at',
            key: 'create_at',
            width: 180,
        },
        {
            title: '操作',
            key: 'action',
            width: 100,
            render: (_, record) => (
                <span
                    style={{ color: '#ff4d4f', cursor: 'pointer' }}
                    onClick={() => handleDelete(record.id)}
                >
                    <DeleteOutlined style={{ marginRight: 8 }} />
                    删除
                </span>
            ),
        },
    ];

    return (
        <Spin spinning={deleteLoading}>
            <div className="ip-blacklist-container">
                {/* 头部 */}
                <div className="ip-header">
                    <span>IP黑名单</span>
                    <Button
                        type="primary"
                        className="orange-button"
                        onClick={() => setModalVisible(true)}
                    >
                        添加IP
                    </Button>
                </div>

                {/* 搜索区域 */}
                <div className="search-area">
                    <Input
                        className="search-item"
                        placeholder="请输入IP"
                        value={filters.ip}
                        onChange={e => setFilters({ ...filters, ip: e.target.value })}
                        allowClear
                    />
                    <div className="location-search-group">
                        <Select
                            value={locationSearchType}
                            onChange={setLocationSearchType}
                            style={{ width: 140 }}
                        >
                            <Select.Option value="province">省份</Select.Option>
                            <Select.Option value="city">城市</Select.Option>
                            <Select.Option value="district">县区</Select.Option>
                        </Select>
                        <Input
                            className="location-input"
                            placeholder={
                                locationSearchType === 'province' ? '请输入省份' :
                                locationSearchType === 'city' ? '请输入城市' :
                                '请输入县区'
                            }
                            value={locationSearchValue}
                            onChange={e => setLocationSearchValue(e.target.value)}
                            allowClear
                        />
                    </div>
                    <div className="search-buttons">
                        <Button
                            type="primary"
                            className="orange-button"
                            onClick={handleSearch}
                        >
                            查询
                        </Button>
                        <Button onClick={handleReset}>重置</Button>
                    </div>
                </div>

                {/* 表格 */}
                <Table
                    columns={columns}
                    dataSource={data}
                    rowKey="id"
                    loading={loading}
                    pagination={false}
                    scroll={{ x: 800 }}
                />

                {/* 分页 */}
                <div className="pagination-container">
                    <Pagination
                        current={currentPage}
                        pageSize={pageSize}
                        total={total}
                        onChange={onPageChange}
                        showSizeChanger
                        showQuickJumper
                        showTotal={total => `共 ${total} 条`}
                    />
                </div>

                {/* 添加IP弹窗 */}
                <Modal
                    title="添加IP"
                    open={modalVisible}
                    onCancel={() => {
                        setModalVisible(false);
                        setIpText('');
                    }}
                    onOk={handleAddIP}
                    confirmLoading={modalLoading}
                    centered
                    width={500}
                    okText="保存"
                    cancelText="取消"
                >
                    <div>
                        <p style={{ marginBottom: 12, color: '#666' }}>请输入黑名单IP，每行一个</p>
                        <textarea
                            value={ipText}
                            onChange={e => setIpText(e.target.value)}
                            placeholder="请输入黑名单IP，每行一个"
                            style={{
                                width: '100%',
                                height: '200px',
                                padding: '8px',
                                borderRadius: '4px',
                                border: '1px solid #d9d9d9',
                                fontFamily: 'monospace',
                                fontSize: '12px',
                            }}
                        />
                    </div>
                </Modal>
            </div>
        </Spin>
    );
};

export default IPBlackList;
